import { cn } from "@btnet/utilities-core";

import { BdsRollupButton, BdsText } from "@buildertrend/components";
import { type BdsRollupButtonProps } from "@buildertrend/components/src/lib/components/BdsRollup/BdsRollup.types";

import "./BdsListPageHeaderFilterButton.less";

const getFilterCountString = (filterCount?: number) => {
    if (!filterCount || filterCount < 1) return undefined;
    return !!filterCount && filterCount > 99 ? "99+" : filterCount?.toString();
};

/**
 * BdsListPageHeaderFilterButton component to show filter count badge on filter button. This is a wrapper around BdsRollupButton.
 *
 * @param props - BdsRollupButtonProps with an additional filterCount prop that indicates the number of active filters. If filterCount is greater than 0, a badge will be displayed on the button.
 * @returns JSX.Element - The rendered BdsListPageHeaderFilterButton component. This should be used within the BdsListPageHeader component to provide filter functionality with visual feedback on active filters.
 */
export const BdsListPageHeaderFilterButton = (
    props: BdsRollupButtonProps & { filterCount?: number },
) => {
    const { filterCount } = props;
    const filterCountStr = getFilterCountString(filterCount);

    return (
        <div className="bds-filter-button--wrapper">
            <BdsRollupButton
                {...props}
                className={cn("bds-filter-button", props.className)}
                root="body"
                boundary="body"
            />
            {/* Only show badge if filterCount is greater than 0 and the button is not loading */}
            {filterCountStr && !props.loading && (
                <span
                    className={cn("bds-filter-button--badge", {
                        "bds-filter-button--badge-disabled": props.disabled,
                    })}
                    aria-label={`${filterCountStr} ${
                        filterCount === 1 ? "filter" : "filters"
                    } applied`}
                >
                    <BdsText className="bds-filter-button--badge-text">{filterCountStr}</BdsText>
                </span>
            )}
        </div>
    );
};
